import { parse as parseYaml } from "yaml";

/** The librarian's per-brain process config, read from `.m8t/brain.yaml`
 *  `processes.librarian`. This module NEVER writes brain.yaml — it is read-only
 *  and parses defensively (any malformed/absent block ⇒ disabled). */
export interface LibrarianConfig {
  enabled: boolean;
  lifecycle: { inboxDays: number; staleDays: number; archiveDays: number };
  artifactRetentionDays: number;
  digestRetentionDays: number;
  /** Per-brain opt-in: auto-merge a promotion PR on an explicit clean `promote` verdict.
   *  STRICT boolean — only the literal `true` enables it; absent / "true" / 1 / malformed ⇒ OFF.
   *  This is the OPERATOR-editable seam (neither the librarian nor the gate write brain.yaml). DEFAULT OFF. */
  autoMerge: boolean;
}

export const DEFAULT_LIFECYCLE = { inboxDays: 14, staleDays: 30, archiveDays: 90 };
export const DEFAULT_RETENTION = { artifactRetentionDays: 30, digestRetentionDays: 90 };

const days = (v: unknown, fallback: number): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const m = /^(\d+)\s*d?$/.exec(v);
    if (m) return Number(m[1]);
  }
  return fallback;
};

/**
 * Parse `processes.librarian.{enabled,lifecycle,artifactRetention,digestRetention}`
 * from a brain.yaml string. Defensive: malformed YAML, an absent/empty block, or
 * wrong-typed fields all degrade to a safe disabled/default config rather than
 * throwing. Mirrors parseDreamerConfig (dream/config.ts) for style consistency.
 */
export function parseLibrarianConfig(rawYaml: string, _env: NodeJS.ProcessEnv): LibrarianConfig {
  let parsed: unknown;
  try {
    parsed = parseYaml(rawYaml);
  } catch {
    return { enabled: false, lifecycle: DEFAULT_LIFECYCLE, ...DEFAULT_RETENTION, autoMerge: false };
  }
  const lib =
    parsed && typeof parsed === "object"
      ? ((parsed as { processes?: { librarian?: Record<string, unknown> } }).processes?.librarian ?? {})
      : {};
  const lc = (lib.lifecycle ?? {}) as Record<string, unknown>;
  return {
    enabled: lib.enabled === true,
    lifecycle: {
      inboxDays: days(lc.inbox, 14),
      staleDays: days(lc.stale, 30),
      archiveDays: days(lc.archive, 90),
    },
    artifactRetentionDays: days(lib.artifactRetention, 30),
    digestRetentionDays: days(lib.digestRetention, 90),
    // STRICT === true (mirrors `enabled`): a truthy-but-not-true value can never silently enable auto-merge.
    autoMerge: lib.autoMerge === true,
  };
}
